"use client";

import {
	Clapperboard,
	Forward,
	Image as ImageIcon,
	Info,
	Loader2,
	Plus,
	RotateCcw,
	Send,
	Settings2,
	Upload,
	X,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
	Conversation,
	ConversationContent,
	ConversationEmptyState,
} from "@/components/ai-elements/conversation";
import { Message, MessageContent } from "@/components/ai-elements/message";
import { Video } from "@/components/ai-elements/video";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import {
	VideoSidebar,
	type VideoHistoryItem,
} from "@/components/playground/video-sidebar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Textarea } from "@/components/ui/textarea";
import { useUser } from "@/hooks/useUser";

import { getVideoCapabilities } from "@routerbench/models";
import { PRICING_CONFIG } from "@routerbench/shared";

import type { Organization, Project } from "@/lib/types";
import type {
	ModelDefinition,
	ProviderDefinition,
	ProviderModelMapping,
} from "@routerbench/models";

interface Frame {
	dataUrl: string;
	name: string;
}

interface Turn {
	localId: string;
	jobId?: string;
	status: "queued" | "in_progress" | "completed" | "failed";
	prompt: string;
	startFrameUrl?: string;
	lastFrameUrl?: string;
	refUrls: string[];
	videoUrl?: string;
	error?: string;
	resolution: string;
	model: string;
	/** The exact request body, kept so the turn can be regenerated. */
	payload?: Record<string, unknown>;
}

interface VideoPageClientProps {
	models: ModelDefinition[];
	providers: ProviderDefinition[];
	organizations: Organization[];
	selectedOrganization: Organization | null;
	projects: Project[];
	selectedProject: Project | null;
}

const ASPECT_RATIOS = ["16:9", "9:16"];

function fmtElapsed(sec: number): string {
	const m = Math.floor(sec / 60);
	const s = sec % 60;
	return `${m}:${s.toString().padStart(2, "0")}`;
}

function readFileAsDataUrl(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () =>
			typeof reader.result === "string"
				? resolve(reader.result)
				: reject(new Error("read failed"));
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(file);
	});
}

/** Capture the first frame of a video (data URL) as a JPEG thumbnail. */
function captureVideoThumb(src: string): Promise<string> {
	return new Promise((resolve, reject) => {
		const v = document.createElement("video");
		v.preload = "metadata";
		v.muted = true;
		v.src = src;
		v.onloadeddata = () => {
			try {
				v.currentTime = Math.min(0.1, (v.duration || 1) / 2);
			} catch {
				/* seek not supported — fall through to onseeked/onerror */
			}
		};
		v.onseeked = () => {
			const canvas = document.createElement("canvas");
			canvas.width = v.videoWidth || 320;
			canvas.height = v.videoHeight || 180;
			const ctx = canvas.getContext("2d");
			if (!ctx) {
				reject(new Error("no canvas context"));
				return;
			}
			ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
			resolve(canvas.toDataURL("image/jpeg", 0.7));
		};
		v.onerror = () => reject(new Error("thumbnail failed"));
	});
}

export default function VideoPageClient({
	models,
	organizations,
	selectedOrganization: initialOrg,
	selectedProject: initialProject,
}: VideoPageClientProps) {
	const { user } = useUser();
	const router = useRouter();
	const isAuthenticated = !!user;

	// Org is user-facing; project is the org's default, picked server-side
	// (switching org navigates with projectId cleared, like the chat playground).
	const selectedOrganization = initialOrg;
	const selectedProject = initialProject;

	const videoModels = useMemo(
		() => models.filter((m) => m.output?.includes("video")),
		[models],
	);
	// Default to Veo 3.1 Fast — it supports every input mode (image, last
	// frame, reference images, extend), so all composer options are available.
	const [selectedModel, setSelectedModel] = useState<string>(
		videoModels.find((m) => m.id === "veo-3.1-fast-generate-preview")?.id ||
			videoModels.find((m) => m.id === "veo-3.0-fast-generate-001")?.id ||
			videoModels[0]?.id ||
			"",
	);

	const mapping = useMemo<ProviderModelMapping | undefined>(() => {
		const baseId = selectedModel.includes("/")
			? selectedModel.split("/", 2)[1]
			: selectedModel;
		const m = videoModels.find((vm) => vm.id === baseId);
		return m?.providers.find(
			(p) => (p as ProviderModelMapping).videoGenerations === true,
		) as ProviderModelMapping | undefined;
	}, [selectedModel, videoModels]);

	// Capabilities (durations, aspect ratios, features) come from catalogue
	// metadata — provider-agnostic, no hardcoded model IDs.
	const caps = useMemo(
		() => (mapping ? getVideoCapabilities(mapping) : null),
		[mapping],
	);
	const supportedResolutions = useMemo(
		() => caps?.resolutions ?? ["720p", "1080p"],
		[caps],
	);
	const supportedAspectRatios = caps?.aspectRatios ?? ASPECT_RATIOS;
	const maxReferenceImages = caps?.referenceImages ?? 0;
	const supportsReference = maxReferenceImages > 0;
	const supportsExtension = caps?.extension ?? false;
	const supportsImageToVideo = caps?.imageToVideo ?? true;
	const supportsInterpolation = caps?.interpolation ?? false;

	// Composer state
	const [prompt, setPrompt] = useState("");
	const [negativePrompt, setNegativePrompt] = useState("");
	const [seed, setSeed] = useState("");
	const [aspectRatio, setAspectRatio] = useState("16:9");
	const [resolution, setResolution] = useState("720p");
	const [duration, setDuration] = useState("8");
	const [startFrame, setStartFrame] = useState<Frame | null>(null);
	const [lastFrame, setLastFrame] = useState<Frame | null>(null);
	const [references, setReferences] = useState<Frame[]>([]);
	// An uploaded video to extend (must be a Veo-generated 720p MP4).
	const [videoFile, setVideoFile] = useState<{
		dataUrl: string;
		name: string;
		thumbUrl?: string;
	} | null>(null);
	// 0–100 while reading the chosen file; null when idle.
	const [videoUploadPct, setVideoUploadPct] = useState<number | null>(null);

	const hasReference = references.length > 0;

	const supportedDurations = useMemo(() => {
		if (resolution !== "720p" || hasReference) {
			return ["8"];
		}
		return caps?.durations ?? ["4", "6", "8"];
	}, [resolution, hasReference, caps]);

	const [turns, setTurns] = useState<Turn[]>([]);
	const [elapsed, setElapsed] = useState(0);

	// Persistent history (from /v1/videos via the gateway).
	const [history, setHistory] = useState<VideoHistoryItem[]>([]);
	const [historyLoading, setHistoryLoading] = useState(false);
	const loadHistory = useCallback(async () => {
		setHistoryLoading(true);
		try {
			const r = await fetch("/api/videos/history?limit=30", {
				cache: "no-store",
			});
			if (!r.ok) {
				return;
			}
			const data = await r.json();
			setHistory(Array.isArray(data?.data) ? data.data : []);
		} catch {
			/* keep prior list */
		} finally {
			setHistoryLoading(false);
		}
	}, []);
	const onPickHistory = useCallback(
		(item: VideoHistoryItem) => {
			if (!item.video?.url) {
				return;
			}
			// Stable id per history item so clicking two different videos adds
			// two distinct turns, and re-clicking the same one doesn't stack
			// duplicates (previously the id used Date.now() and grew on every click).
			const histLocalId = `hist-${item.id}`;
			setTurns((prev) => {
				if (prev.some((t) => t.localId === histLocalId)) {
					return prev;
				}
				return [
					...prev,
					{
						localId: histLocalId,
						jobId: item.id,
						status: "completed",
						prompt: item.prompt || "(no prompt)",
						refUrls: [],
						resolution: item.usage?.resolution || "720p",
						model: item.metadata?.used_model || "",
						videoUrl: `/api/videos/${item.id}/content`,
					},
				];
			});
			// Reflect the selected clip in the URL (each is its own route).
			const params = new URLSearchParams(window.location.search);
			params.set("clip", item.id);
			router.replace(`/video?${params.toString()}`, { scroll: false });
			// Always scroll the picked clip into view (appended or deduped) so a
			// click reliably shows it.
			requestAnimationFrame(() => {
				const el = document.getElementById(`turn-${histLocalId}`);
				el?.scrollIntoView({ behavior: "smooth", block: "center" });
			});
		},
		[router],
	);

	// Deep-link: open ?clip=<id> once history has loaded (one-shot).
	const clipRestoredRef = useRef(false);
	useEffect(() => {
		if (clipRestoredRef.current || history.length === 0) {
			return;
		}
		const clipId = new URLSearchParams(window.location.search).get("clip");
		if (!clipId) {
			return;
		}
		const item = history.find((h) => h.id === clipId);
		if (item && item.status === "completed" && item.video?.url) {
			clipRestoredRef.current = true;
			onPickHistory(item);
		}
	}, [history, onPickHistory]);

	const clearClipParam = useCallback(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.has("clip")) {
			params.delete("clip");
			const qs = params.toString();
			router.replace(qs ? `/video?${qs}` : "/video", { scroll: false });
		}
	}, [router]);
	// jobId of the clip the next prompt continues (Veo extension). Set to the
	// latest completed 720p clip so the same window keeps building one video;
	// null means the next prompt starts a fresh video.
	const [continueFromId, setContinueFromId] = useState<string | null>(null);

	const activeTurn = turns.find(
		(t) => (t.status === "queued" || t.status === "in_progress") && t.jobId,
	);
	const busy = turns.some(
		(t) => t.status === "queued" || t.status === "in_progress",
	);
	// Continuation is only possible on extension-capable models (Veo 3.1 / Fast).
	const canContinue = !!continueFromId && supportsExtension;

	// Keep resolution/duration valid for the selected model.
	useEffect(() => {
		if (!supportedResolutions.includes(resolution)) {
			setResolution(supportedResolutions[0]);
		}
	}, [supportedResolutions, resolution]);
	useEffect(() => {
		if (!supportedDurations.includes(duration)) {
			setDuration(supportedDurations[supportedDurations.length - 1]);
		}
	}, [supportedDurations, duration]);

	// Drop unsupported inputs when switching models.
	useEffect(() => {
		if (!supportsReference && references.length > 0) {
			setReferences([]);
		}
		if (!supportsImageToVideo && startFrame) {
			setStartFrame(null);
			setLastFrame(null);
		}
		if (!supportsInterpolation && lastFrame) {
			setLastFrame(null);
		}
		if (!supportsExtension && videoFile) {
			setVideoFile(null);
		}
	}, [
		supportsReference,
		supportsImageToVideo,
		supportsInterpolation,
		supportsExtension,
		references.length,
		startFrame,
		lastFrame,
		videoFile,
	]);

	// Ensure a playground API key for the selected project.
	const ensuredProjectRef = useRef<string | null>(null);
	const [keyReady, setKeyReady] = useState(false);
	useEffect(() => {
		if (!isAuthenticated || !selectedProject) {
			ensuredProjectRef.current = null;
			setKeyReady(false);
			return;
		}
		if (ensuredProjectRef.current === selectedProject.id) {
			setKeyReady(true);
			return;
		}
		setKeyReady(false);
		fetch("/api/ensure-playground-key", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ projectId: selectedProject.id }),
		})
			.then(() => {
				ensuredProjectRef.current = selectedProject.id;
				setKeyReady(true);
			})
			.catch(() => {
				/* Generate stays disabled until a key is ready */
			});
	}, [isAuthenticated, selectedProject]);

	// Initial history load once a key is ready.
	useEffect(() => {
		if (keyReady) {
			void loadHistory();
		}
	}, [keyReady, loadHistory]);

	// Poll the active turn until it completes/fails.
	useEffect(() => {
		if (!activeTurn?.jobId) {
			return;
		}
		const jobId = activeTurn.jobId;
		const localId = activeTurn.localId;
		let cancelled = false;
		const patch = (p: Partial<Turn>) =>
			setTurns((prev) =>
				prev.map((t) => (t.localId === localId ? { ...t, ...p } : t)),
			);
		const tick = async () => {
			try {
				const res = await fetch(`/api/videos/${jobId}`);
				if (!res.ok || cancelled) {
					return;
				}
				const data = await res.json();
				if (cancelled) {
					return;
				}
				if (data.status === "completed") {
					patch({
						status: "completed",
						videoUrl: `/api/videos/${jobId}/content`,
					});
					// Point the next prompt at this clip so the same window keeps
					// building one video (Veo can only extend 720p clips).
					if (activeTurn.resolution === "720p") {
						setContinueFromId(jobId);
					}
					void loadHistory();
				} else if (data.status === "failed") {
					patch({ status: "failed", error: data.error?.message });
					toast.error("Video generation failed");
					void loadHistory();
				} else {
					patch({ status: data.status });
				}
			} catch {
				/* keep polling */
			}
		};
		const interval = setInterval(tick, 5000);
		void tick();
		return () => {
			cancelled = true;
			clearInterval(interval);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTurn?.jobId, activeTurn?.status]);

	// Elapsed timer for the active turn (drives the progress bar).
	useEffect(() => {
		if (!activeTurn) {
			return;
		}
		setElapsed(0);
		const t = setInterval(() => setElapsed((e) => e + 1), 1000);
		return () => clearInterval(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTurn?.localId]);

	const expectedSeconds = /fast|lite/.test(selectedModel) ? 60 : 150;
	const progress = Math.min(95, Math.round((elapsed / expectedSeconds) * 100));

	const estInr = useMemo(() => {
		if (!mapping) {
			return 0;
		}
		let perSecond = mapping.videoPricePerSecond ?? 0;
		const tiers = mapping.videoPricingTiers;
		if (tiers && tiers.length > 0) {
			perSecond = (tiers.find((t) => t.resolution === resolution) ?? tiers[0])
				.pricePerSecond;
		}
		return perSecond * Number(duration) * PRICING_CONFIG.usdToInrRate;
	}, [mapping, resolution, duration]);

	const canGenerate =
		keyReady &&
		!busy &&
		videoUploadPct === null &&
		!!selectedProject &&
		!!selectedModel &&
		!!prompt.trim();

	// ── Attachment handlers ──────────────────────────────────────────────
	const onPickStartFrame = useCallback(async (file: File | null) => {
		if (!file) {
			return;
		}
		if (file.size > 8 * 1024 * 1024) {
			toast.error("Image too large (max 8MB)");
			return;
		}
		setStartFrame({ dataUrl: await readFileAsDataUrl(file), name: file.name });
		setReferences([]);
	}, []);

	const onPickLastFrame = useCallback(async (file: File | null) => {
		if (!file) {
			return;
		}
		if (file.size > 8 * 1024 * 1024) {
			toast.error("Image too large (max 8MB)");
			return;
		}
		setLastFrame({ dataUrl: await readFileAsDataUrl(file), name: file.name });
	}, []);

	const onAddReferences = useCallback(
		async (files: FileList | null) => {
			if (!files) {
				return;
			}
			const room = maxReferenceImages - references.length;
			const picked = Array.from(files).slice(0, Math.max(0, room));
			const next: Frame[] = [];
			for (const file of picked) {
				if (file.size > 8 * 1024 * 1024) {
					toast.error(`${file.name} too large (max 8MB)`);
					continue;
				}
				next.push({ dataUrl: await readFileAsDataUrl(file), name: file.name });
			}
			if (next.length) {
				setReferences((prev) =>
					[...prev, ...next].slice(0, maxReferenceImages),
				);
				setStartFrame(null);
				setLastFrame(null);
			}
		},
		[maxReferenceImages, references.length],
	);

	// Upload a video to extend (clears the other, mutually-exclusive inputs).
	const onPickVideo = useCallback((file: File | null) => {
		if (!file) {
			return;
		}
		if (file.size > 60 * 1024 * 1024) {
			toast.error("Video too large (max 60MB)");
			return;
		}
		setStartFrame(null);
		setLastFrame(null);
		setReferences([]);
		setVideoUploadPct(0);
		const reader = new FileReader();
		reader.onprogress = (e) => {
			if (e.lengthComputable) {
				setVideoUploadPct(Math.round((e.loaded / e.total) * 100));
			}
		};
		reader.onerror = () => {
			toast.error("Failed to read video");
			setVideoUploadPct(null);
		};
		reader.onload = async () => {
			const dataUrl = reader.result;
			if (typeof dataUrl !== "string") {
				setVideoUploadPct(null);
				return;
			}
			const thumbUrl = await captureVideoThumb(dataUrl).catch(() => undefined);
			setVideoFile({ dataUrl, name: file.name, thumbUrl });
			setVideoUploadPct(null);
		};
		reader.readAsDataURL(file);
	}, []);

	// ── Submit ───────────────────────────────────────────────────────────
	const submitJob = useCallback(
		async (payload: Record<string, unknown>, turn: Turn) => {
			setTurns((prev) => [...prev, { ...turn, payload }]);
			try {
				const r = await fetch("/api/videos", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(payload),
				});
				const data = await r.json();
				if (!r.ok) {
					// Gateway returns { error: true, status, message } (boolean error);
					// the proxy returns { error: "<string>" }. Pick the readable string.
					const msg =
						(typeof data?.error === "string" && data.error) ||
						data?.message ||
						"Failed to start generation";
					toast.error(msg);
					setTurns((prev) =>
						prev.map((t) =>
							t.localId === turn.localId
								? { ...t, status: "failed", error: msg }
								: t,
						),
					);
					return;
				}
				setTurns((prev) =>
					prev.map((t) =>
						t.localId === turn.localId ? { ...t, jobId: data.id } : t,
					),
				);
				// The job row is created synchronously, so it shows in history
				// right away (status updates as it polls).
				void loadHistory();
			} catch (e) {
				const msg = e instanceof Error ? e.message : "Request failed";
				toast.error(msg);
				setTurns((prev) =>
					prev.map((t) =>
						t.localId === turn.localId
							? { ...t, status: "failed", error: msg }
							: t,
					),
				);
			}
		},
		[loadHistory],
	);

	const handleGenerate = useCallback(() => {
		if (!canGenerate) {
			return;
		}
		// A fresh generation isn't a history pick — drop any stale ?clip.
		clearClipParam();
		const text = prompt.trim();
		const localId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

		// Extend an uploaded video (must be a Veo-generated 720p MP4).
		if (videoFile) {
			void submitJob(
				{ model: selectedModel, prompt: text, video: videoFile.dataUrl },
				{
					localId,
					status: "queued",
					prompt: `↳ Extend uploaded video: ${text}`,
					refUrls: [],
					resolution: "720p",
					model: selectedModel,
				},
			);
			setPrompt("");
			setVideoFile(null);
			return;
		}

		// Same window → continue the current video via Veo extension (prompt only).
		if (canContinue && continueFromId) {
			void submitJob(
				{ model: selectedModel, prompt: text, source_video_id: continueFromId },
				{
					localId,
					status: "queued",
					prompt: `↳ ${text}`,
					refUrls: [],
					resolution: "720p",
					model: selectedModel,
				},
			);
			setPrompt("");
			return;
		}

		// A last frame needs a start frame (Veo interpolates start → last).
		if (lastFrame && !startFrame) {
			toast.error("Add a start frame too — last frame needs both");
			return;
		}

		// Fresh generation with all inputs/settings.
		const seedNum = seed.trim() ? Number(seed.trim()) : undefined;
		void submitJob(
			{
				model: selectedModel,
				prompt: text,
				aspect_ratio: aspectRatio,
				resolution,
				duration,
				...(negativePrompt.trim()
					? { negative_prompt: negativePrompt.trim() }
					: {}),
				...(seedNum !== undefined && !Number.isNaN(seedNum)
					? { seed: seedNum }
					: {}),
				...(hasReference
					? { reference_images: references.map((r) => r.dataUrl) }
					: startFrame
						? {
								image: startFrame.dataUrl,
								...(lastFrame ? { last_frame: lastFrame.dataUrl } : {}),
							}
						: {}),
			},
			{
				localId,
				status: "queued",
				prompt: text,
				startFrameUrl: startFrame?.dataUrl,
				lastFrameUrl: lastFrame?.dataUrl,
				refUrls: references.map((r) => r.dataUrl),
				resolution,
				model: selectedModel,
			},
		);
		// Clear the composer (keep settings + model).
		setPrompt("");
		setStartFrame(null);
		setLastFrame(null);
		setReferences([]);
	}, [
		canGenerate,
		canContinue,
		continueFromId,
		videoFile,
		prompt,
		seed,
		startFrame,
		lastFrame,
		references,
		hasReference,
		resolution,
		duration,
		aspectRatio,
		negativePrompt,
		selectedModel,
		submitJob,
		clearClipParam,
	]);

	// "Continue from here" on an older clip — point the next prompt at it.
	const handleContinueFrom = useCallback((source: Turn) => {
		if (!source.jobId) {
			return;
		}
		setContinueFromId(source.jobId);
	}, []);

	// Re-run a previous turn with the exact same request.
	const handleRegenerate = useCallback(
		(source: Turn) => {
			if (busy || !source.payload) {
				return;
			}
			const turn: Turn = {
				localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
				status: "queued",
				prompt: source.prompt,
				startFrameUrl: source.startFrameUrl,
				lastFrameUrl: source.lastFrameUrl,
				refUrls: source.refUrls,
				resolution: source.resolution,
				model: source.model,
			};
			void submitJob(source.payload, turn);
		},
		[busy, submitJob],
	);

	// Clear the conversation and composer to start a brand-new video.
	const handleNewVideo = useCallback(() => {
		setTurns([]);
		setContinueFromId(null);
		setPrompt("");
		setNegativePrompt("");
		setSeed("");
		setStartFrame(null);
		setLastFrame(null);
		setReferences([]);
		setVideoFile(null);
		setElapsed(0);
		clearClipParam();
	}, [clearClipParam]);

	const thumb = (src: string, key: string) => (
		<img
			key={key}
			src={src}
			alt=""
			className="h-12 w-12 rounded-md object-cover"
		/>
	);

	return (
		<SidebarProvider>
			<div className="flex h-svh w-full overflow-hidden bg-background">
				<VideoSidebar
					selectedOrganization={selectedOrganization}
					history={history}
					historyLoading={historyLoading}
					onPickHistory={onPickHistory}
				/>
				<div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
					{/* Header */}
					<div className="flex shrink-0 items-center gap-2 border-b px-3 py-2">
						<SidebarTrigger className="shrink-0" />
						<Clapperboard className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
						<span className="hidden text-sm font-medium sm:inline">Video</span>
						<div className="mx-1 hidden h-5 w-px shrink-0 bg-border/60 sm:block" />
						<div className="min-w-0 flex-1 sm:max-w-[260px]">
							<Select
								value={selectedModel}
								onValueChange={setSelectedModel}
								disabled={busy}
							>
								<SelectTrigger className="h-9 w-full">
									<SelectValue placeholder="Select a video model" />
								</SelectTrigger>
								<SelectContent>
									{videoModels.map((m) => (
										<SelectItem key={m.id} value={m.id}>
											{m.name || m.id}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* Organization — switching navigates so the server picks the
						    org's default project (project key is project-scoped). */}
						<div className="ml-auto hidden md:block">
							<Select
								value={selectedOrganization?.id ?? ""}
								onValueChange={(id) => router.push(`/video?orgId=${id}`)}
								disabled={busy}
							>
								<SelectTrigger className="h-8 w-[170px]">
									<SelectValue placeholder="Organization" />
								</SelectTrigger>
								<SelectContent>
									{organizations.map((o) => (
										<SelectItem key={o.id} value={o.id}>
											{o.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<Button
							variant="outline"
							size="sm"
							className="ml-auto h-8 shrink-0 gap-1.5 md:ml-2"
							onClick={handleNewVideo}
							disabled={turns.length === 0 && !prompt.trim()}
							title="Clear and start a new video"
						>
							<Plus className="h-4 w-4" />
							<span className="hidden sm:inline">New video</span>
						</Button>
						<div className="shrink-0">
							<ThemeToggle />
						</div>
					</div>

					{/* Conversation */}
					<Conversation>
						<ConversationContent className="mx-auto w-full max-w-3xl">
							{turns.length === 0 ? (
								<ConversationEmptyState
									icon={<Clapperboard className="h-10 w-10" />}
									title="Generate a video"
									description="Describe a scene, optionally attach a start/last frame, and hit generate."
								/>
							) : (
								turns.map((turn) => (
									<div key={turn.localId} id={`turn-${turn.localId}`}>
										{/* User turn */}
										<Message from="user">
											<MessageContent variant="flat">
												<div className="whitespace-pre-wrap">{turn.prompt}</div>
												{(turn.startFrameUrl ||
													turn.lastFrameUrl ||
													turn.refUrls.length > 0) && (
													<div className="flex flex-wrap gap-2 pt-1">
														{turn.startFrameUrl &&
															thumb(turn.startFrameUrl, "s")}
														{turn.lastFrameUrl && thumb(turn.lastFrameUrl, "l")}
														{turn.refUrls.map((u, i) => thumb(u, `r${i}`))}
													</div>
												)}
											</MessageContent>
										</Message>

										{/* Assistant turn */}
										<Message from="assistant">
											<MessageContent
												variant="flat"
												className="w-full max-w-md"
											>
												{turn.status === "completed" && turn.videoUrl ? (
													<div className="flex w-full flex-col gap-2">
														<div className="flex aspect-video w-full items-center justify-center overflow-hidden rounded-md border border-border/60 bg-black shadow-sm">
															<Video
																src={turn.videoUrl}
																className="h-full max-h-full w-full rounded-none object-contain"
															/>
														</div>

														<div className="flex flex-wrap items-center justify-between gap-2">
															<a
																href={turn.videoUrl}
																download={`${turn.jobId}.mp4`}
																className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
															>
																Download MP4
															</a>
															<div className="flex items-center gap-1.5">
																<Button
																	variant="ghost"
																	size="sm"
																	className="h-8"
																	disabled={busy || !turn.payload}
																	onClick={() => handleRegenerate(turn)}
																	title="Regenerate with the same settings"
																>
																	<RotateCcw className="mr-1 h-3.5 w-3.5" />
																	Regenerate
																</Button>
																{supportsExtension &&
																	turn.resolution === "720p" &&
																	continueFromId !== turn.jobId && (
																		<Button
																			variant="outline"
																			size="sm"
																			className="h-8"
																			disabled={busy}
																			onClick={() => handleContinueFrom(turn)}
																			title="Make the next prompt continue this clip"
																		>
																			<Forward className="mr-1 h-3.5 w-3.5" />
																			Continue from here
																		</Button>
																	)}
															</div>
														</div>
													</div>
												) : turn.status === "failed" ? (
													<div className="flex w-full flex-col gap-2">
														<div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
															{turn.error ? turn.error : "Generation failed."}
														</div>
														{turn.payload && (
															<div>
																<Button
																	variant="outline"
																	size="sm"
																	className="h-8"
																	disabled={busy}
																	onClick={() => handleRegenerate(turn)}
																>
																	<RotateCcw className="mr-1 h-3.5 w-3.5" />
																	Try again
																</Button>
															</div>
														)}
													</div>
												) : (
													<div className="flex w-full flex-col gap-2">
														<div className="relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-md border border-border/60 bg-muted/50">
															<div className="absolute inset-0 animate-pulse bg-muted/40" />
															<div className="relative flex flex-col items-center gap-2 text-center">
																<Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
																<span className="text-sm font-semibold text-foreground">
																	{turn.status === "queued"
																		? "Queued…"
																		: "Rendering…"}
																</span>
																{turn === activeTurn && (
																	<span className="text-xs tabular-nums text-muted-foreground">
																		{fmtElapsed(elapsed)} elapsed
																	</span>
																)}
															</div>
														</div>
														<Progress
															value={turn === activeTurn ? progress : 5}
															className="h-1.5"
														/>
													</div>
												)}
											</MessageContent>
										</Message>
									</div>
								))
							)}
						</ConversationContent>
					</Conversation>

					{/* Composer */}
					<div className="shrink-0 border-t bg-background px-4 py-3">
						<div className="mx-auto w-full max-w-3xl">
							{canContinue && (
								<div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-muted/40 px-3 py-1.5 text-xs">
									<span className="flex items-center gap-1.5 font-medium">
										<Forward className="h-3.5 w-3.5 text-muted-foreground" />
										Continuing your video — each prompt adds ~7s
									</span>
									<button
										type="button"
										onClick={() => setContinueFromId(null)}
										className="text-muted-foreground underline underline-offset-2 hover:text-foreground"
									>
										Start fresh
									</button>
								</div>
							)}
							{videoUploadPct !== null && (
								<div className="mb-2 flex flex-col gap-1 rounded-lg border border-border/60 bg-muted/40 px-3 py-2">
									<div className="flex items-center justify-between text-xs">
										<span className="flex items-center gap-1.5 font-medium">
											<Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
											Reading video…
										</span>
										<span className="tabular-nums text-muted-foreground">
											{videoUploadPct}%
										</span>
									</div>
									<Progress value={videoUploadPct} className="h-1.5" />
								</div>
							)}
							{videoFile && videoUploadPct === null && (
								<div className="mb-2 flex items-center gap-3 rounded-lg border border-border/60 bg-muted/40 p-2">
									{videoFile.thumbUrl ? (
										<video
											src={videoFile.dataUrl}
											poster={videoFile.thumbUrl}
											muted
											playsInline
											className="h-14 w-24 shrink-0 rounded-md bg-black object-cover"
										/>
									) : (
										<div className="flex h-14 w-24 shrink-0 items-center justify-center rounded-md bg-black">
											<Clapperboard className="h-5 w-5 text-muted-foreground" />
										</div>
									)}
									<div className="min-w-0 flex-1">
										<div className="truncate text-xs font-medium">
											{videoFile.name}
										</div>
										<div className="text-[11px] text-muted-foreground">
											Will be extended (+~7s). Must be a Veo-generated 720p MP4.
										</div>
									</div>
									<button
										type="button"
										onClick={() => setVideoFile(null)}
										className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-muted"
									>
										<X className="h-3.5 w-3.5" />
									</button>
								</div>
							)}
							{!canContinue &&
								!videoFile &&
								(startFrame || lastFrame || references.length > 0) && (
									<div className="mb-2 flex flex-wrap items-center gap-2">
										{startFrame && (
											<FrameChip
												frame={startFrame}
												label="Start frame"
												onRemove={() => {
													setStartFrame(null);
													setLastFrame(null);
												}}
											/>
										)}
										{lastFrame && (
											<FrameChip
												frame={lastFrame}
												label="Last frame"
												onRemove={() => setLastFrame(null)}
											/>
										)}
										{references.map((ref, i) => (
											<FrameChip
												key={ref.dataUrl}
												frame={ref}
												label={`Ref ${i + 1}`}
												onRemove={() =>
													setReferences((prev) =>
														prev.filter((_, j) => j !== i),
													)
												}
											/>
										))}
									</div>
								)}

							<div className="rounded-2xl border bg-card p-2 shadow-sm">
								<Textarea
									value={prompt}
									onChange={(e) => setPrompt(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter" && !e.shiftKey) {
											e.preventDefault();
											handleGenerate();
										}
									}}
									placeholder={
										canContinue
											? "Describe what happens next…"
											: "Describe your video… (e.g. cinematic drone shot over a misty valley at dawn)"
									}
									className="min-h-[56px] resize-none border-0 bg-transparent shadow-none focus-visible:ring-0"
								/>
								<div className="flex items-center justify-between gap-2 px-1 pt-1">
									<div className="flex items-center gap-1">
										{/* Frame/reference/settings only apply to a fresh video —
										    extension (continue or uploaded) takes a prompt only. */}
										{!canContinue && !videoFile && supportsImageToVideo && (
											<IconUpload
												title={
													supportsInterpolation
														? "Start frame (first frame / image-to-video)"
														: "Start image (image-to-video)"
												}
												onFiles={(f) => onPickStartFrame(f?.[0] ?? null)}
											>
												<ImageIcon className="h-4 w-4" />
												<span className="text-[11px] font-medium">
													{supportsInterpolation ? "Start frame" : "Image"}
												</span>
											</IconUpload>
										)}
										{!canContinue && !videoFile && supportsInterpolation && (
											<IconUpload
												title="Last frame — needs a start frame; Veo fills the motion from start → last"
												onFiles={(f) => onPickLastFrame(f?.[0] ?? null)}
											>
												<ImageIcon className="h-4 w-4" />
												<span className="text-[11px] font-medium">
													Last frame
												</span>
											</IconUpload>
										)}
										{!canContinue &&
											!videoFile &&
											supportsReference &&
											references.length < maxReferenceImages && (
												<IconUpload
													title={`Reference images (up to ${maxReferenceImages})`}
													multiple
													onFiles={(f) => onAddReferences(f)}
												>
													<Upload className="h-4 w-4" />
												</IconUpload>
											)}
										{/* Upload a video to extend (Veo 3.1 / Fast, Veo-made 720p MP4) */}
										{!canContinue && supportsExtension && !videoFile && (
											<IconUpload
												title="Upload a video to extend (Veo-generated 720p MP4)"
												accept="video/mp4,video/*"
												onFiles={(f) => onPickVideo(f?.[0] ?? null)}
											>
												<Clapperboard className="h-4 w-4" />
											</IconUpload>
										)}

										{/* Settings (fresh video only — extension is 720p, prompt-only) */}
										{!canContinue && (
											<Popover>
												<PopoverTrigger asChild>
													<Button
														type="button"
														variant="ghost"
														size="sm"
														className="h-8 gap-1.5 px-2 text-xs"
													>
														<Settings2 className="h-4 w-4" />
														{aspectRatio} · {resolution} · {duration}s
													</Button>
												</PopoverTrigger>
												<PopoverContent
													align="start"
													className="w-72 space-y-3"
												>
													<SettingRow label="Aspect ratio">
														<Select
															value={aspectRatio}
															onValueChange={setAspectRatio}
														>
															<SelectTrigger className="h-8">
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																{supportedAspectRatios.map((a) => (
																	<SelectItem key={a} value={a}>
																		{a}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
													</SettingRow>
													<SettingRow label="Resolution">
														<Select
															value={resolution}
															onValueChange={setResolution}
														>
															<SelectTrigger className="h-8">
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																{supportedResolutions.map((r) => (
																	<SelectItem key={r} value={r}>
																		{r}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
													</SettingRow>
													<SettingRow label="Duration">
														<Select
															value={duration}
															onValueChange={setDuration}
															disabled={supportedDurations.length <= 1}
														>
															<SelectTrigger className="h-8">
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																{supportedDurations.map((d) => (
																	<SelectItem key={d} value={d}>
																		{d}s
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
													</SettingRow>
													<div className="flex flex-col gap-1.5">
														<Label className="text-xs">Negative prompt</Label>
														<Textarea
															value={negativePrompt}
															onChange={(e) =>
																setNegativePrompt(e.target.value)
															}
															placeholder="blurry, low quality…"
															className="min-h-[52px] text-sm"
														/>
													</div>
													<div className="flex flex-col gap-1.5">
														<Label className="text-xs">Seed</Label>
														<input
															inputMode="numeric"
															value={seed}
															onChange={(e) =>
																setSeed(e.target.value.replace(/[^0-9]/g, ""))
															}
															placeholder="random"
															className="h-8 rounded-md border bg-transparent px-3 text-sm"
														/>
													</div>
												</PopoverContent>
											</Popover>
										)}

										{/* Input formats & limits */}
										<Popover>
											<PopoverTrigger asChild>
												<Button
													type="button"
													variant="ghost"
													size="icon"
													className="h-8 w-8 text-muted-foreground"
													title="Input formats & limits"
												>
													<Info className="h-4 w-4" />
												</Button>
											</PopoverTrigger>
											<PopoverContent
												align="start"
												className="w-80 text-[12px] leading-relaxed"
											>
												<div className="mb-1.5 text-xs font-semibold">
													Input formats & limits
												</div>
												<ul className="space-y-1.5 text-muted-foreground">
													<li>
														<span className="font-medium text-foreground">
															Images
														</span>{" "}
														(start / last frame, references): PNG, JPEG or WEBP,
														up to 8&nbsp;MB each.
													</li>
													<li>
														<span className="font-medium text-foreground">
															Last frame
														</span>{" "}
														needs a start frame and is Veo&nbsp;3.1 only.
													</li>
													<li>
														<span className="font-medium text-foreground">
															Reference images
														</span>{" "}
														keep a subject consistent (up to{" "}
														{Math.max(maxReferenceImages, 3)}, Veo&nbsp;3.1 /
														Fast). Forces an 8s clip.
													</li>
													<li>
														<span className="font-medium text-foreground">
															Extend
														</span>{" "}
														works on a finished 720p clip only (Veo-generated,
														16:9 or 9:16, within 2 days).
													</li>
													<li>
														1080p / 4k and reference images require an 8-second
														duration.
													</li>
												</ul>
											</PopoverContent>
										</Popover>
									</div>

									<div className="flex items-center gap-2">
										{estInr > 0 && (
											<span className="hidden text-xs text-muted-foreground sm:inline">
												~${estInr.toFixed(2)}
											</span>
										)}
										<Button
											size="icon"
											className="h-9 w-9 rounded-full"
											disabled={!canGenerate}
											onClick={handleGenerate}
										>
											{busy ? (
												<Loader2 className="h-4 w-4 animate-spin" />
											) : (
												<Send className="h-4 w-4" />
											)}
										</Button>
									</div>
								</div>
							</div>
							<p className="mt-1 px-1 text-[11px] text-muted-foreground">
								Generation takes ~15s–6min. Press Enter to generate, Shift+Enter
								for a new line.
							</p>
						</div>
					</div>
				</div>
			</div>
		</SidebarProvider>
	);
}

function FrameChip({
	frame,
	label,
	onRemove,
}: {
	frame: Frame;
	label: string;
	onRemove: () => void;
}) {
	return (
		<div className="relative flex items-center gap-2 rounded-lg border bg-card py-1 pl-1 pr-2">
			<img
				src={frame.dataUrl}
				alt={frame.name}
				className="h-9 w-9 rounded object-cover"
			/>
			<span className="text-xs text-muted-foreground">{label}</span>
			<button
				type="button"
				onClick={onRemove}
				className="rounded-full p-0.5 hover:bg-muted"
			>
				<X className="h-3 w-3" />
			</button>
		</div>
	);
}

function IconUpload({
	title,
	multiple,
	accept = "image/*",
	onFiles,
	children,
}: {
	title: string;
	multiple?: boolean;
	accept?: string;
	onFiles: (files: FileList | null) => void;
	children: React.ReactNode;
}) {
	return (
		<label
			title={title}
			className="inline-flex h-8 min-w-8 cursor-pointer items-center justify-center gap-1 rounded-md px-2 text-muted-foreground hover:bg-muted hover:text-foreground"
		>
			{children}
			<input
				type="file"
				accept={accept}
				multiple={multiple}
				className="hidden"
				onChange={(e) => onFiles(e.target.files)}
			/>
		</label>
	);
}

function SettingRow({
	label,
	children,
}: {
	label: string;
	children: React.ReactNode;
}) {
	return (
		<div className="flex items-center justify-between gap-3">
			<Label className="text-xs">{label}</Label>
			<div className="w-36">{children}</div>
		</div>
	);
}
